import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Clock, Check, AlertTriangle, Ticket } from "lucide-react";
import { API, formatErr } from "../lib/api";

function fmtCountdown(deadline) {
    const ms = new Date(deadline).getTime() - Date.now();
    if (ms <= 0) return "0:00";
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function ClaimInvite() {
    const { code } = useParams();
    const nav = useNavigate();
    const [state, setState] = useState(null); // {recipient_exercises, recipient_progress, claim_deadline_at, recipient_complete}
    const [loading, setLoading] = useState(true);
    const [errMsg, setErrMsg] = useState(null);
    const [, force] = useState(0);

    const start = async () => {
        setLoading(true);
        setErrMsg(null);
        try {
            const r = await axios.post(`${API}/auth/claim-invite/start`, { code });
            if (r.data.kind === "legacy") {
                // Old-style admin invite, no proof flow — bounce straight to register
                nav(`/register?code=${code}`);
                return;
            }
            setState(r.data);
        } catch (e) {
            setErrMsg(formatErr(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { start(); /* eslint-disable-next-line */ }, [code]);
    useEffect(() => {
        const t = setInterval(() => force((v) => v + 1), 1000);
        return () => clearInterval(t);
    }, []);

    const allDone = state?.recipient_complete;
    const expired = useMemo(() => {
        if (!state?.claim_deadline_at) return false;
        return new Date(state.claim_deadline_at).getTime() <= Date.now();
    }, [state, force]); // eslint-disable-line

    const logRep = async (exType, count) => {
        try {
            const r = await axios.post(`${API}/auth/claim-invite/progress`, {
                code, exercise_type: exType, count: Number(count) || 0,
            });
            setState((s) => ({
                ...s,
                recipient_progress: r.data.recipient_progress,
                recipient_complete: r.data.recipient_complete,
            }));
            if (r.data.recipient_complete) {
                toast.success("Proof complete — you can register.");
            }
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    return (
        <div className="min-h-screen bg-charcoal-950 text-white px-5 pt-12 pb-20" data-testid="claim-page">
            <div className="max-w-md mx-auto">
                <div className="flex items-center gap-2">
                    <Ticket size={16} className="text-neon-green" />
                    <span className="label-cap text-[10px] text-neon-green">Earned Invite</span>
                </div>
                <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">
                    Prove You Belong
                </h1>
                <div className="font-mono text-zinc-400 tracking-widest mt-1">{code}</div>

                {loading && <div className="mt-10 text-center text-zinc-500 text-sm">Loading...</div>}

                {errMsg && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 surface-1 p-4 border-l-2 border-l-neon-orange">
                        <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle size={14} className="text-neon-orange" />
                            <span className="label-cap text-xs text-neon-orange">Not yet</span>
                        </div>
                        <p className="text-sm text-zinc-300">{errMsg}</p>
                    </motion.div>
                )}

                {state && !errMsg && (
                    <>
                        <div className="mt-6 surface-2 p-4">
                            <div className="flex items-center justify-between">
                                <span className="label-cap text-[10px] text-zinc-500 flex items-center gap-1">
                                    <Clock size={11} /> Window
                                </span>
                                <span
                                    className="font-display font-black text-2xl tabular-nums text-neon-orange"
                                    data-testid="claim-countdown"
                                >
                                    {fmtCountdown(state.claim_deadline_at)}
                                </span>
                            </div>
                            {expired && (
                                <div className="text-xs text-neon-orange mt-2">
                                    Window expired. <button onClick={start} className="underline">Restart</button>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 space-y-3">
                            {state.recipient_exercises.map((ex) => (
                                <ClaimRepRow
                                    key={ex.exercise_type}
                                    ex={ex}
                                    progress={(state.recipient_progress || {})[ex.exercise_type] || 0}
                                    disabled={expired || allDone}
                                    onLog={(n) => logRep(ex.exercise_type, n)}
                                />
                            ))}
                        </div>

                        {allDone && (
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="mt-6 surface-2 p-5 border-l-2 border-l-neon-green"
                                data-testid="claim-success"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Check size={16} className="text-neon-green" />
                                    <span className="label-cap text-xs text-neon-green">Proof Locked</span>
                                </div>
                                <p className="text-sm text-zinc-300 mb-4">
                                    You've earned your spot. Finish registration to enter the arena.
                                </p>
                                <button
                                    onClick={() => nav(`/register?code=${code}`)}
                                    data-testid="claim-register-btn"
                                    className="btn-neon w-full py-3"
                                >
                                    Complete Registration →
                                </button>
                            </motion.div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function ClaimRepRow({ ex, progress, disabled, onLog }) {
    const [v, setV] = useState("");
    const done = progress >= ex.reps_required;
    return (
        <div className="surface-1 p-3" data-testid={`claim-row-${ex.exercise_type}`}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="font-bold capitalize text-sm">{ex.exercise_type}</span>
                <span className="text-[11px] text-zinc-500">{progress} / {ex.reps_required}</span>
            </div>
            <div className="w-full h-1 bg-charcoal-700 mb-2 overflow-hidden">
                <div
                    className={`h-full transition-all ${done ? "bg-neon-green" : "bg-neon-orange"}`}
                    style={{ width: `${Math.min(100, (progress / ex.reps_required) * 100)}%` }}
                />
            </div>
            {!done && (
                <div className="flex gap-2">
                    <input
                        type="number"
                        min={1}
                        value={v}
                        placeholder="+reps"
                        onChange={(e) => setV(e.target.value)}
                        disabled={disabled}
                        className="flex-1 bg-charcoal-900 border border-charcoal-500 px-2 py-1.5 text-sm"
                    />
                    <button
                        onClick={() => { if (v) { onLog(v); setV(""); } }}
                        disabled={disabled || !v}
                        data-testid={`claim-log-${ex.exercise_type}`}
                        className="px-3 py-1.5 text-xs label-cap bg-neon-green text-black disabled:opacity-40"
                    >
                        Log
                    </button>
                </div>
            )}
            {done && <div className="text-xs text-neon-green flex items-center gap-1"><Check size={12} /> Done</div>}
        </div>
    );
}
